import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { theme } from '@/constants/theme';
import { RuntimeSessionGuard } from '@/features/unified-learning/runtime/screens/RuntimeSessionGuard';
import { RuntimeAsyncState } from '@/features/unified-learning/runtime/components/RuntimeAsyncState';
import { buildRuntimePracticeSessionPath, buildRuntimeReviewDetailPath, type RuntimeBase } from '@/features/unified-learning/runtime/runtimeRoutes';
import { loadMistakeReviewHubData } from '@/features/unified-learning/runtime/mistakeReviewService';
import { trackKursiPlayEvent } from '@/utils/kursiPlayTelemetry';
import AnimatedList from '@/components/premium/AnimatedList';

const BG = '#0B0B14';
const SURFACE = '#16142A';
const BORDER = '#2D2B50';
const TEXT = '#F0EFF8';
const TEXT_MED = '#9896B4';
const PRIMARY = theme.colors.primary;

const tierCopy: Record<string, string> = {
  weak: 'חלש',
  risky: 'מסוכן',
};

export function RuntimeMistakeReviewScreen({ runtimeBase }: { runtimeBase: RuntimeBase }) {
  const { slug, uid, token } = useLocalSearchParams<{ slug: string; uid: string; token?: string }>();
  const safeSlug = typeof slug === 'string' ? slug : '';
  const safeUid = typeof uid === 'string' ? uid : '';
  const runtimeSession = { uid: safeUid, token: token ? String(token) : undefined };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<Awaited<ReturnType<typeof loadMistakeReviewHubData>> | null>(null);

  const load = useCallback(async () => {
    if (!safeSlug || !safeUid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await loadMistakeReviewHubData(safeSlug, safeUid);
      setData(result);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'שגיאה בטעינת סקירת טעויות');
    } finally {
      setLoading(false);
    }
  }, [safeSlug, safeUid]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!safeSlug || !safeUid) return;
    void trackKursiPlayEvent('learn_review_entered', { slug: safeSlug, uid: safeUid, source: 'review_screen' });
  }, [safeSlug, safeUid]);

  const practiceFallbackLessons = useMemo(
    () => data?.weakTopics.map((w) => w.lessonId).slice(0, 2) ?? [],
    [data],
  );

  const goPractice = (lessonIds: string[], mode: string) => {
    if (!safeSlug || !safeUid || lessonIds.length === 0) return;
    router.push(buildRuntimePracticeSessionPath(runtimeBase, safeSlug, runtimeSession, {
      mode,
      lesson_ids: lessonIds.join(','),
    }) as any);
  };

  if (!safeSlug || !safeUid) {
    return <RuntimeSessionGuard runtimeBase={runtimeBase} slug={safeSlug} />;
  }

  if (loading) return <RuntimeAsyncState state="loading" title="טוען נתונים..." description="אנחנו אוספים את נתוני המפגש האחרון שלך." />;
  if (error || !data) {
    return (
      <RuntimeAsyncState
        state="error"
        title="לא הצלחנו לטעון את סקירת הטעויות"
        description="כנראה שיש עומס רגעי או בעיית חיבור. אפשר לנסות שוב, או לחזור לתרגול ולהמשיך לצבור נתונים."
        retryLabel="טען שוב את הסקירה"
        onRetry={() => void load()}
      />
    );
  }

  return (
    <ScrollView style={s.root} contentContainerStyle={s.content}>
      <View style={s.topContext}>
        <Pressable style={s.backBtn} onPress={() => router.back()}>
          <Text style={s.backBtnText}>← חזרה</Text>
        </Pressable>
        <Text style={s.eyebrow}>KURSI LEARN · סקירת טעויות</Text>
      </View>

      <View style={s.hero}>
        <Text style={s.title}>סקירת טעויות</Text>
        <Text style={s.subtitle}>פה מתקנים טעויות לעומק: מבינים למה זה קרה ובונים חזרה מהירה לביטחון.</Text>
        <View style={s.metricsRow}>
          <View style={s.metricCard}>
            <Text style={s.metricValue}>{data.recentMistakes.length}</Text>
            <Text style={s.metricLabel}>טעויות אחרונות</Text>
          </View>
          <View style={s.metricCard}>
            <Text style={s.metricValue}>{data.weakTopics.length}</Text>
            <Text style={s.metricLabel}>נושאים חלשים</Text>
          </View>
        </View>
      </View>

      {!data.hasHistory && (
        <View style={s.empty}>
          <Text style={s.emptyTitle}>אין עדיין היסטוריית טעויות</Text>
          <Text style={s.emptyBody}>ברגע שתצבור ניסיונות, נציג כאן נקודות חולשה, טעויות אחרונות ופעולות תיקון ממוקדות.</Text>
          <Pressable style={s.primaryBtn} onPress={() => goPractice(practiceFallbackLessons, 'review_bootstrap')}>
            <Text style={s.primaryBtnText}>התחל תרגול לבניית היסטוריה</Text>
          </Pressable>
        </View>
      )}

      {data.weakTopics.length > 0 && (
        <View style={s.card}>
          <Text style={s.cardTitle}>נושאים חלשים</Text>
          {data.weakTopics.slice(0, 4).map((topic) => (
            <View key={topic.lessonId} style={s.topicRow}>
              <Text style={s.topicMeta}>{topic.weakCount + topic.riskyCount} טעויות · ציון {topic.avgScore.toFixed(2)}</Text>
              <Text style={s.topicTitle}>{topic.lessonTitle}</Text>
            </View>
          ))}
          <Pressable
            style={[s.secondaryBtn, data.retryReadyLessonIds.length === 0 && { opacity: 0.45 }]}
            onPress={() => goPractice(data.retryReadyLessonIds, 'mistake_weak_topics')}
            disabled={data.retryReadyLessonIds.length === 0}
          >
            <Text style={s.secondaryBtnText}>תרגל אזורים חלשים →</Text>
          </Pressable>
        </View>
      )}

      <View style={s.card}>
        <Text style={s.cardTitle}>טעויות אחרונות</Text>
        {data.recentMistakes.length === 0 && <Text style={s.emptyBody}>אין כרגע טעויות חלש/מסוכן להצגה.</Text>}
        <AnimatedList
          items={data.recentMistakes}
          renderItem={(m) => (
            <Pressable
              key={m.attemptId}
              style={s.mistakeCard}
              onPress={() => router.push(buildRuntimeReviewDetailPath(runtimeBase, safeSlug, m.attemptId, runtimeSession) as any)}
            >
              <Text style={s.mistakeTier}>{tierCopy[m.qualityTier] ?? m.qualityTier}</Text>
              <Text style={s.mistakeTitle}>{m.scenarioTitle}</Text>
              <Text style={s.mistakeSub}>{m.lessonTitle}</Text>
              <Text style={s.mistakeSub} numberOfLines={2}>בחירה קודמת: {m.chosenActionLabel}</Text>
              <Text style={s.mistakeLink}>פתח תיקון →</Text>
            </Pressable>
          )}
        />
      </View>
    </ScrollView>
  );
}

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: BG },
  content: { padding: 20, gap: 14, maxWidth: 680, width: '100%', alignSelf: 'center' as any, paddingBottom: 40, paddingTop: 30 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: BG },
  topContext: { gap: 10, alignItems: 'flex-end' },
  backBtn: { borderWidth: 1, borderColor: BORDER, backgroundColor: '#121126', borderRadius: 999, paddingVertical: 8, paddingHorizontal: 14 },
  backBtnText: { color: TEXT_MED, fontFamily: theme.fonts.medium, fontSize: 12 },
  eyebrow: { color: PRIMARY, fontFamily: theme.fonts.bold, fontSize: 12 },
  hero: { backgroundColor: '#121126', borderWidth: 1, borderColor: BORDER, borderRadius: 16, padding: 16, gap: 10 },
  title: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 30 },
  subtitle: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 14, lineHeight: 21 },
  metricsRow: { flexDirection: 'row-reverse', gap: 10 },
  metricCard: { flex: 1, backgroundColor: SURFACE, borderColor: BORDER, borderWidth: 1, borderRadius: 12, padding: 10, alignItems: 'flex-end' },
  metricValue: { color: PRIMARY, fontFamily: theme.fonts.bold, fontSize: 20 },
  metricLabel: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 12 },
  card: { backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER, borderRadius: 14, padding: 14, gap: 10 },
  cardTitle: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 18, textAlign: 'right' },
  topicRow: { borderTopWidth: 1, borderTopColor: BORDER, paddingTop: 8 },
  topicTitle: { color: TEXT, fontFamily: theme.fonts.semiBold, textAlign: 'right' },
  topicMeta: { color: TEXT_MED, fontFamily: theme.fonts.regular, textAlign: 'right', fontSize: 12 },
  mistakeCard: { borderWidth: 1, borderColor: BORDER, borderRadius: 12, padding: 10, gap: 4 },
  mistakeTier: { color: '#FCA5A5', fontFamily: theme.fonts.bold, textAlign: 'right', fontSize: 12 },
  mistakeTitle: { color: TEXT, fontFamily: theme.fonts.bold, textAlign: 'right' },
  mistakeSub: { color: TEXT_MED, fontFamily: theme.fonts.regular, textAlign: 'right', fontSize: 12 },
  mistakeLink: { color: PRIMARY, fontFamily: theme.fonts.bold, textAlign: 'right', marginTop: 2 },
  primaryBtn: { backgroundColor: PRIMARY, borderRadius: 10, alignItems: 'center', paddingVertical: 10, marginTop: 8 },
  primaryBtnText: { color: '#fff', fontFamily: theme.fonts.bold },
  secondaryBtn: { borderWidth: 1, borderColor: PRIMARY, borderRadius: 10, alignItems: 'center', paddingVertical: 10 },
  secondaryBtnText: { color: PRIMARY, fontFamily: theme.fonts.bold },
  empty: { backgroundColor: '#141326', borderWidth: 1, borderColor: BORDER, borderRadius: 12, padding: 14 },
  emptyTitle: { color: TEXT, fontFamily: theme.fonts.bold, textAlign: 'right' },
  emptyBody: { color: TEXT_MED, fontFamily: theme.fonts.regular, textAlign: 'right' },
  error: { color: '#FCA5A5', fontFamily: theme.fonts.bold, textAlign: 'center' },
  retryBtn: { backgroundColor: PRIMARY, paddingHorizontal: 20, paddingVertical: 10, borderRadius: 10, marginTop: 10 },
  retryText: { color: '#fff', fontFamily: theme.fonts.bold },
});
